import { useEffect, useLayoutEffect, useState } from 'react';
import { MDSDivider, MDSLoadingIndicator, MDSSnackbar, MDSTypography } from '@marqvision/mds-v2';
import { SimpleTextEditor } from '../../SimpleTextEditor';
import { Comment, AppInfo, ListingCommentPanelProps } from '../@types';
import { MessageItem } from './@components/Messages';
import { usePseudoOptimisticUpdate } from './@hooks/usePseudoOptimisticUpdate';
import { Styled } from './styles';
import { getRepliesLabel } from './@utils';

type Props<R extends Comment[]> = {
  listingId: number;
  appInfo: AppInfo;
  parentComment: Comment;
  apiHook: Omit<ListingCommentPanelProps<Comment[], R>['apiHook'], 'getComments'>;

  scrollPositionUpdate: () => void;
  onSendSuccess?: () => void;
  onSendError?: () => void;
  onDelete?: () => void;
};
export const ThreadView = <R extends Comment[]>(props: Props<R>) => {
  const { parentComment, apiHook, listingId, scrollPositionUpdate, appInfo } = props;

  const { data, isLoading, isError, refetch, isSuccess, isFetching } = apiHook.getReplies(listingId, parentComment.id);

  // note-@jamie: 수동 optimistic update를 위해 임시 데이터를 저장한다
  const { pendingCommentsByType, resolvedComments, addPendingComment, updatePendingComment, clearPendingComments } =
    usePseudoOptimisticUpdate(data);

  const removeCommentMutation = apiHook.removeComment();
  const handleDelete = async (messageId: number) => {
    addPendingComment({ id: messageId } as Comment, 'deleting');
    const isSuccess = await removeCommentMutation.mutateAsync({
      messageId,
      listingId,
    });
    if (isSuccess) {
      updatePendingComment(messageId, 'fetching-after-delete');
      props.onDelete?.();
      refetch();
      MDSSnackbar({
        title: 'Deleted',
        type: 'complete',
      });
      return true;
    }
  };

  const [showRootMessageAsDeleted, setShowRootMessageAsDeleted] = useState(parentComment.deleted);
  const handleDeleteRootMessage = async (messageId: number) => {
    await handleDelete(messageId);
    setShowRootMessageAsDeleted(true);
  };

  useEffect(() => {
    // 매 전송마다 밣생
    if (isSuccess && !isFetching) {
      clearPendingComments();
    }
  }, [scrollPositionUpdate, isSuccess, isFetching, clearPendingComments]);

  useLayoutEffect(() => {
    scrollPositionUpdate();
  }, [resolvedComments, scrollPositionUpdate]);

  const currentReplyCount = resolvedComments?.length ?? 0;

  return isLoading ? (
    <Styled.LoadingStateBox>
      <MDSLoadingIndicator size={48} strokeWidth={4} />
    </Styled.LoadingStateBox>
  ) : isError ? (
    <Styled.LoadingStateBox>{/* 적당한 화면이 필요하다 */}</Styled.LoadingStateBox>
  ) : (
    <Styled.PanelContent viewMode={'thread'}>
      <Styled.ScrollableContent viewMode={'thread'}>
        <Styled.CommentContainer>
          <MessageItem
            key={parentComment.id}
            comment={{
              ...parentComment,
              deleted: showRootMessageAsDeleted,
            }}
            isThread
            onDelete={handleDeleteRootMessage}
            appInfo={appInfo}
          />
        </Styled.CommentContainer>

        {/* reply_count === 0 일 때에도 안보여준다 */}
        {currentReplyCount > 0 ? (
          <>
            <Styled.ThreadDividerBox>
              <MDSTypography
                variant="body"
                size="xs"
                weight="medium"
                wordBreak="keep-all"
                overflowWrap="normal"
                color="color/content/neutral/secondary/normal"
              >
                {getRepliesLabel(currentReplyCount)}
              </MDSTypography>
              <MDSDivider orientation="horizontal" />
            </Styled.ThreadDividerBox>
            <Styled.CommentContainer>
              {resolvedComments.map((reply) => (
                <MessageItem key={reply.id} comment={reply} isThread onDelete={handleDelete} appInfo={appInfo} />
              ))}
            </Styled.CommentContainer>
          </>
        ) : null}
      </Styled.ScrollableContent>
      {!appInfo.isReadOnly && (
        <Styled.FixedTextEditor viewMode={'thread'}>
          <SimpleTextEditor
            appInfo={appInfo}
            apiHook={{
              getPresignedUrl: apiHook.getPresignedUrl,
              addComment: apiHook.addComment,
            }}
            isNewMessageLoading={pendingCommentsByType.adding.length > 0}
            listingIds={[listingId]}
            commentId={parentComment.id}
            onSendStart={(newReply: Comment) => {
              addPendingComment(newReply, 'adding');
              setTimeout(() => {
                scrollPositionUpdate();
              }, 30);
            }}
            onSendSuccess={() => {
              refetch();
              props.onSendSuccess?.();
            }}
            onSendError={() => {
              clearPendingComments();
              props.onSendError?.();
            }}
          />
        </Styled.FixedTextEditor>
      )}
    </Styled.PanelContent>
  );
};
